import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import OptionsManager from "./OptionsManager";
import HideValueModal from "./HideValueModal";
import { useHideValueGate } from "../hooks/useHideValueGate";
import { useSeededLookupOptions } from "../hooks/useSeededLookupOptions";
import { useLookupValues, RawLookupItem } from "../hooks/useLookupValues";
import { getLookupFieldConfig } from "../lookupFieldRegistry";
import { OptionsManagerData, OptionItem } from "../utils/fieldFormUtils";

// Stable no-op selector so the "no parent" branch doesn't churn useLookupValues.
const EMPTY_SELECT: (data: unknown) => RawLookupItem[] = () => [];

interface LookupOptionsPanelProps {
  // Field key (e.g. "industryId") whose registry entry drives this panel.
  fieldKey: string;
  onOptionsChange: (data: OptionsManagerData) => void;
  onHiddenToggle?: (id: number | string) => void;
  initialDefaultValue?: string;
}

/** One editor for every lookup-backed configurable system field, driven entirely by its LOOKUP_FIELDS registry entry. */
const LookupOptionsPanel: React.FC<LookupOptionsPanelProps> = ({
  fieldKey,
  onOptionsChange,
  onHiddenToggle,
  initialDefaultValue = ""
}) => {
  const { t } = useTranslation();
  const config = getLookupFieldConfig(fieldKey);
  const parent = config?.parent;
  const [selectedParentId, setSelectedParentId] = useState<
    number | string | null
  >(null);

  // Parent picker options — only fetched for scoped fields.
  const { items: parentItems } = useLookupValues({
    route: parent?.listRoute ?? "",
    selectItems: parent?.selectItems ?? EMPTY_SELECT,
    enabled: !!parent
  });

  // The field's values, scoped by the chosen parent when applicable.
  const { items, isFetching } = useLookupValues({
    route: config?.listRoute ?? "",
    parentPathParam: parent?.pathParam,
    parentId: selectedParentId,
    selectItems: config?.selectItems ?? EMPTY_SELECT,
    enabled: !!config
  });

  // Thread the selected parent into every change so the save knows the scope.
  const handleOptionsChange = useCallback(
    (data: OptionsManagerData) => {
      onOptionsChange(parent ? { ...data, selectedParentId } : data);
    },
    [onOptionsChange, parent, selectedParentId]
  );

  const { initialOptions, initialOptionItems, hasItems } =
    useSeededLookupOptions({
      optionItems: items,
      skipSeed: (!!parent && selectedParentId === null) || isFetching,
      onOptionsChange: handleOptionsChange,
      defaultValue: initialDefaultValue,
      // Re-seed on parent switch even if the two industries' items look identical.
      scopeKey: selectedParentId
    });

  const { onBeforeHide, hidePrompt, cancelHide, confirmHide } = useHideValueGate(
    useCallback(
      (item: OptionItem) => {
        if (item.id === undefined || !config) return null;
        if (parent && selectedParentId === null) return null;
        return {
          route: config.usageRoute,
          pathParams: config.buildUsagePathParams(item.id, selectedParentId)
        };
      },
      [config, parent, selectedParentId]
    )
  );

  if (!config) return null;

  const showOptions = (!parent || selectedParentId !== null) && !isFetching;

  return (
    <div className="flex flex-col gap-4">
      {parent ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center">
            <Text variant="bodySecondary">
              {t(parent.label.key, parent.label.fallback)}
            </Text>
            <Text variant="bodySecondary" className="text-primary ml-1">
              *
            </Text>
          </div>
          <Text variant="bodyTertiary" className="text-sm">
            {t(parent.helpText.key, parent.helpText.fallback)}
          </Text>
          <SingleSelect
            fieldName="lookupParentFilter"
            options={parentItems
              .filter((item) => item.id !== undefined)
              .map((item) => ({
                id: item.id as number | string,
                displayName: item.name
              }))}
            selectedValue={selectedParentId ?? ""}
            // Coerce to number only when numeric; preserve a non-numeric parent id.
            onSelection={(value) => {
              const numericId = Number(value);
              setSelectedParentId(Number.isNaN(numericId) ? value : numericId);
            }}
            placeholder={t(parent.placeholder.key, parent.placeholder.fallback)}
            variant="form"
          />
        </div>
      ) : null}
      {showOptions ? (
        <OptionsManager
          key={selectedParentId ?? "root"}
          initialOptions={initialOptions}
          initialOptionItems={initialOptionItems}
          initialAllowUsersToAdd={false}
          initialDefaultValue={initialDefaultValue}
          fieldType="single"
          onOptionsChange={handleOptionsChange}
          onHiddenToggle={onHiddenToggle}
          onBeforeHide={onBeforeHide}
          // Scoped fields with no values yet fall back to a regular editor.
          isEditableSystemField={parent ? hasItems : true}
        />
      ) : null}
      {hidePrompt ? (
        <HideValueModal
          open
          valueName={hidePrompt.item.name}
          onCancel={cancelHide}
          onConfirm={confirmHide}
        />
      ) : null}
    </div>
  );
};

export default LookupOptionsPanel;
